import type {
  AssetSale,
  AvailableAssetSale,
  BackendInvestorAllocation,
  Distribution,
  DistributionListResponse,
  DistributionSummary,
  FundMetricsImpact,
  FundWaterfallTerms,
  RawAssetSale,
  RawAvailableAssetSale,
  RawBackendInvestorAllocation,
  RawDistribution,
  RawDistributionListResponse,
  RawDistributionSummary,
  RawFundMetricsImpact,
  RawFundWaterfallTerms,
  RawWaterfallCalculationResponse,
  RawWaterfallTier,
  RawWizardInvestorAllocation,
  WaterfallCalculationResponse,
  WaterfallTier,
  WizardInvestorAllocation,
} from '@/types/distribution';
import { toNumber, toNumberOrNull } from '@/utils/number-operations';

function mapAssetSale(raw: RawAssetSale): AssetSale {
  return {
    id: raw.id,
    portfolio_company_name: raw.portfolio_company_name,
    transaction_name: raw.transaction_name,
    transaction_date: raw.transaction_date,
    sale_amount: toNumberOrNull(raw.sale_amount),
  };
}

function mapBackendAllocation(
  raw: RawBackendInvestorAllocation
): BackendInvestorAllocation {
  return {
    investor_fund_relationship_id: raw.investor_fund_relationship_id,
    investor_name: raw.investor_name,
    commitment_amount: toNumber(raw.commitment_amount),
    commitment_percentage: toNumber(raw.commitment_percentage),
    amount: toNumber(raw.amount),
    return_of_capital: toNumber(raw.return_of_capital),
    preferred_return: toNumber(raw.preferred_return),
    catch_up: toNumber(raw.catch_up),
    carried_interest_lp: toNumber(raw.carried_interest_lp),
    carried_interest_gp: toNumber(raw.carried_interest_gp),
  };
}

function mapWaterfallTier(raw: RawWaterfallTier): WaterfallTier {
  return {
    name: raw.name,
    description: raw.name,
    amount: toNumber(raw.amount),
    percentage: toNumber(raw.percentage),
  };
}

function mapFundWaterfallTerms(raw: RawFundWaterfallTerms): FundWaterfallTerms {
  return {
    waterfall_type: raw.waterfall_type,
    hurdle_rate: toNumber(raw.hurdle_rate),
    carried_interest: toNumber(raw.carried_interest),
    gp_catch_up: toNumber(raw.gp_catch_up),
  };
}

function mapFundMetricsImpact(raw: RawFundMetricsImpact): FundMetricsImpact {
  return {
    total_distributed_before: toNumber(raw.total_distributed_before),
    total_distributed_after: toNumber(raw.total_distributed_after),
    dpi_before: toNumber(raw.dpi_before),
    dpi_after: toNumber(raw.dpi_after),
    nav_before: toNumber(raw.nav_before),
    nav_after: toNumber(raw.nav_after),
    gp_carry_before: toNumber(raw.gp_carry_before),
    gp_carry_after: toNumber(raw.gp_carry_after),
  };
}

function mapWizardAllocation(
  raw: RawWizardInvestorAllocation
): WizardInvestorAllocation {
  return {
    investor_id: raw.investor_id,
    investor_fund_relationship_id: raw.investor_fund_relationship_id,
    investor_name: raw.investor_name,
    commitment_amount: toNumber(raw.commitment_amount),
    commitment_percentage: toNumber(raw.commitment_percentage),
    return_of_capital: toNumber(raw.return_of_capital),
    preferred_return: toNumber(raw.preferred_return),
    catch_up: toNumber(raw.catch_up),
    carried_interest_lp: toNumber(raw.carried_interest_lp),
    carried_interest_gp: toNumber(raw.carried_interest_gp),
    total_distribution: toNumber(raw.total_distribution),
    has_custom_terms: raw.has_custom_terms,
    custom_hurdle_rate: toNumberOrNull(raw.custom_hurdle_rate),
    custom_carried_interest: toNumberOrNull(raw.custom_carried_interest),
    custom_catch_up_rate: toNumberOrNull(raw.custom_catch_up_rate),
    bank_account_verified: raw.bank_account_verified,
    payment_status: raw.payment_status,
  };
}

export function mapDistribution(raw: RawDistribution): Distribution {
  return {
    id: raw.id,
    fund_id: raw.fund_id,
    fund_name: raw.fund_name,
    distribution_number: raw.distribution_number,
    status: raw.status,
    distribution_date: raw.distribution_date,
    currency: raw.currency,
    amount: toNumber(raw.amount),
    portfolio_company_id: raw.portfolio_company_id,
    portfolio_company_name: raw.portfolio_company_name,
    asset_sales: (raw.asset_sales ?? []).map(mapAssetSale),
    description: raw.description,
    notes: raw.notes,
    created_by: raw.created_by,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
    payment_due_date: raw.payment_due_date,
    exit_value: raw.exit_value != null ? toNumber(raw.exit_value) : undefined,
    total_expenses:
      raw.total_expenses != null ? toNumber(raw.total_expenses) : undefined,
    shares_sold: toNumberOrNull(raw.shares_sold),
    price_per_share: toNumberOrNull(raw.price_per_share),
    cost_basis: toNumberOrNull(raw.cost_basis),
    cost_per_share: toNumberOrNull(raw.cost_per_share),
    waterfall_calculated: raw.waterfall_calculated,
    waterfall_tiers: raw.waterfall_tiers?.map(mapWaterfallTier) ?? null,
    investor_allocations: raw.investor_allocations?.map(mapBackendAllocation),
    total_investors: raw.total_investors,
    investors_paid: raw.investors_paid,
    bank_transaction_id: raw.bank_transaction_id,
    bank_transaction_matched: raw.bank_transaction_matched,
    approved_by: raw.approved_by,
    approved_at: raw.approved_at,
    fund_metrics_impact: raw.fund_metrics_impact
      ? mapFundMetricsImpact(raw.fund_metrics_impact)
      : null,
  };
}

function mapDistributionSummary(
  raw: RawDistributionSummary
): DistributionSummary {
  return {
    id: raw.id,
    fund_id: raw.fund_id,
    fund_name: raw.fund_name,
    distribution_number: raw.distribution_number,
    status: raw.status,
    distribution_date: raw.distribution_date,
    amount: toNumber(raw.amount),
    currency: raw.currency,
    description: raw.description,
    created_at: raw.created_at,
    fund_transaction_status: raw.fund_transaction_status,
    exit_value: raw.exit_value != null ? toNumber(raw.exit_value) : undefined,
    total_investors: raw.total_investors,
    investors_paid: raw.investors_paid,
    bank_transaction_matched: raw.bank_transaction_matched,
    fund_waterfall_terms: raw.fund_waterfall_terms
      ? mapFundWaterfallTerms(raw.fund_waterfall_terms)
      : null,
  };
}

export function mapDistributionListResponse(
  raw: RawDistributionListResponse
): DistributionListResponse {
  return {
    items: raw.items.map(mapDistributionSummary),
    total_count: raw.total_count,
    page: raw.page,
    page_size: raw.page_size,
  };
}

export function mapAvailableAssetSale(
  raw: RawAvailableAssetSale
): AvailableAssetSale {
  return {
    id: raw.id,
    transaction_name: raw.transaction_name,
    portfolio_company_id: raw.portfolio_company_id,
    portfolio_company_name: raw.portfolio_company_name,
    transaction_date: raw.transaction_date,
    sale_amount: toNumberOrNull(raw.sale_amount),
    transaction_status: raw.transaction_status,
  };
}

export function mapWaterfallCalculationResponse(
  raw: RawWaterfallCalculationResponse
): WaterfallCalculationResponse {
  return {
    waterfall_tiers: raw.waterfall_tiers.map(mapWaterfallTier),
    investor_allocations: raw.investor_allocations.map(mapWizardAllocation),
    fund_terms: {
      hurdle_rate: toNumber(raw.fund_terms.hurdle_rate),
      carried_interest: toNumber(raw.fund_terms.carried_interest),
      catch_up_rate: toNumber(raw.fund_terms.catch_up_rate),
      waterfall_type: raw.fund_terms.waterfall_type,
    },
    total_lp_distribution: toNumber(raw.total_lp_distribution),
    total_gp_distribution: toNumber(raw.total_gp_distribution),
  };
}
